import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { trpcPricingListPriceZones, trpcPricingListPriceZonesWithGeoAreas } from '@trashlab/api/src/generated/hooks';
import mapboxgl from 'mapbox-gl';
import Map, { MapRef, NavigationControl, Source, Layer, CircleLayer, FillLayer, LineLayer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import env from '@trashlab/viteapp/src/env';
import { FileArchive, CircleDot, Info, MapPin } from 'lucide-react';
import { initialRadiusBasedCenter } from '@trashlab/viteapp/src/components/pricing/radius-based-pricing-map-configurator';
import { useTrashlabOrganization, useTrashlabOrganizationOpen } from '@trashlab/viteapp/src/hooks/shared/use-trashlab-organization';
import * as turf from '@turf/turf';
import type { FeatureCollection, Feature, Polygon, Point } from 'geojson';

// Import Shadcn components
import { Button } from '@trashlab/viteapp/src/components/ui/button';
import { Badge } from '@trashlab/viteapp/src/components/ui/badge';
import { safeGetNumber, isJsonObject, JsonValue } from '@trashlab/api/src/library/server/utils/json-compatibility';

// Updated interface for zones with embedded geoAreas
interface ZoneMapVisualizationProps {
  zones: typeof trpcPricingListPriceZonesWithGeoAreas.output;
  selectedZoneId: string | null;
  onZoneSelect: (zoneId: string) => void;
  isPublic?: boolean;
  tenantId?: string;
}

// GeoJSON type definitions
interface ZoneProperties {
  zoneId: string;
  zoneName: string;
  isActive: boolean;
  color?: string;
}

const ZoneMapVisualization = ({
  zones,
  selectedZoneId,
  onZoneSelect,
  isPublic = false,
  tenantId,
}: ZoneMapVisualizationProps) => {
  const mapRef = useRef<MapRef>(null);
  const [highlightedZoneId, setHighlightedZoneId] = useState<string | null>(
    null,
  );
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Mapbox access token
  const mapboxAccessToken = env.NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN;

  // Fetch organization data for initial map center
  const organization = isPublic && tenantId ? useTrashlabOrganizationOpen({ tenantId }) : useTrashlabOrganization();

  // Initialize with the default center
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    initialRadiusBasedCenter,
  );
  const [geocoded, setGeocoded] = useState(false);
  const [mapZoom, setMapZoom] = useState(8);

  // Filter for active zones
  const activeZones = zones.filter((zone) => !zone.archivedAtTs);
  const radiusBasedZones = activeZones.filter(
    (zone) => zone.type === 'RADIUS_BASED',
  );
  const zipCodeBasedZones = activeZones.filter(
    (zone) => zone.type === 'ZIP_CODE',
  );

  // Count zones with geoareas
  const zonesWithGeoAreas = activeZones.filter(
    (zone) => (zone.geoAreas && zone.geoAreas.length > 0) || zone.type === 'GEO_AREA',
  );

  // Determine active zone (highlighted or selected)
  const activeZoneId = highlightedZoneId || selectedZoneId;

  // Create GeoJSON source data for radius-based zones
  const radiusBasedZonesGeoJSON = useMemo((): FeatureCollection<
    Polygon,
    ZoneProperties
  > => {
    return {
      type: 'FeatureCollection',
      features: radiusBasedZones
        .filter(
          (zone) =>
            zone.latitude !== null &&
            zone.latitude !== undefined &&
            zone.longitude !== null &&
            zone.longitude !== undefined &&
            zone.radius !== null &&
            zone.radius !== undefined,
        )
        .map((zone) => {
          const center = [zone.longitude as number, zone.latitude as number];
          const outerCircle = turf.circle(
            center,
            (zone.radius as number) * 1609.34, // Convert miles to meters
            {
              steps: 64,
              units: 'meters',
              properties: {
                zoneId: zone.id,
                zoneName: zone.name,
                isActive: zone.id === activeZoneId,
                color: zone.color || '#3B82F6', // Use zone color or default blue
              },
            },
          );

          const minRadius = Math.max(zone.minRadius ?? 0, 0);
          if (minRadius <= 0) {
            return outerCircle;
          }

          const innerCircle = turf.circle(center, minRadius * 1609.34, {
            steps: 64,
            units: 'meters',
          });

          return {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [
                outerCircle.geometry.coordinates[0],
                [...innerCircle.geometry.coordinates[0]].reverse(),
              ],
            },
            properties: {
              zoneId: zone.id,
              zoneName: zone.name,
              isActive: zone.id === activeZoneId,
              color: zone.color || '#3B82F6',
            },
          } as Feature<Polygon, ZoneProperties>;
        }),
    };
  }, [radiusBasedZones, activeZoneId]);

  // Create GeoJSON source data for zip code zones
  const zipCodeZonesGeoJSON = useMemo((): FeatureCollection<
    Point,
    ZoneProperties
  > => {
    return {
      type: 'FeatureCollection',
      features: zipCodeBasedZones
        .filter((zone) => zone.latitude && zone.longitude)
        .map((zone) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [zone.longitude as number, zone.latitude as number],
          },
          properties: {
            zoneId: zone.id,
            zoneName: zone.name,
            isActive: zone.id === activeZoneId,
            color: zone.color || '#3B82F6', // Use zone color or default blue
          },
        })),
    };
  }, [zipCodeBasedZones, activeZoneId]);

  // Create GeoJSON source data for geo areas
  const geoAreasGeoJSON = useMemo((): FeatureCollection => {
    return {
      type: 'FeatureCollection',
      features: zonesWithGeoAreas
        .flatMap((zone) => {
          if (!zone.geoAreas || zone.geoAreas.length === 0) {
            return [];
          }

          return zone.geoAreas.flatMap((geoArea, areaIndex) => {
            if (!geoArea.area) {
              return [];
            }

            try {
              // Convert area to GeoJSON features
              // Note: geoArea.area is an array of polygons
              return geoArea.area
                .map((polygon, polyIndex) => {
                  if (!polygon || polygon.length === 0) {
                    return null;
                  }

                  return {
                    type: 'Feature',
                    geometry: {
                      type: 'Polygon',
                      coordinates: polygon,
                    },
                    properties: {
                      zoneId: zone.id,
                      zoneName: zone.name,
                      isActive: zone.id === activeZoneId,
                      color: zone.color || '#3B82F6', // Use zone color or default blue
                    },
                  };
                })
                .filter(Boolean);
            } catch (error) {
              console.error('Error rendering geoarea polygon:', error);
              return [];
            }
          });
        })
        .filter(Boolean) as Feature[],
    };
  }, [zonesWithGeoAreas, activeZoneId]);

  // Style for radius-based zones
  const radiusZoneLayer: FillLayer = {
    id: 'radius-zones',
    type: 'fill',
    paint: {
      'fill-color': [
        'case',
        ['get', 'isActive'],
        ['get', 'color'], // Use the color property when active
        ['get', 'color'], // Use the color property when inactive too
      ],
      'fill-opacity': [
        'case',
        ['get', 'isActive'],
        0.4, // More opaque when active
        0.2, // Less opaque when inactive
      ],
      'fill-outline-color': ['get', 'color'],
    },
  };

  // Style for radius-based zone outlines
  const radiusZoneOutlineLayer: LineLayer = {
    id: 'radius-zones-outline',
    type: 'line',
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'case',
        ['get', 'isActive'],
        3, // Thicker for active
        2, // Normal for inactive
      ],
      'line-opacity': [
        'case',
        ['get', 'isActive'],
        1, // Full opacity for active
        0.8, // Slightly transparent for inactive
      ],
    },
  };

  // Style for zip code zones
  const zipCodeZoneLayer: CircleLayer = {
    id: 'zip-code-zones',
    type: 'circle',
    paint: {
      'circle-radius': 15,
      'circle-color': ['get', 'color'],
      'circle-opacity': [
        'case',
        ['get', 'isActive'],
        0.8, // More opaque for active
        0.6, // Less opaque for inactive
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': ['get', 'color'],
      'circle-stroke-opacity': [
        'case',
        ['get', 'isActive'],
        1, // Full opacity for active
        0.8, // Slightly transparent for inactive
      ],
    },
  };

  // Style for geo areas
  const geoAreaLayer: FillLayer = {
    id: 'geo-areas',
    type: 'fill',
    paint: {
      'fill-color': ['get', 'color'],
      'fill-opacity': [
        'case',
        ['get', 'isActive'],
        0.4, // More opaque for active
        0.3, // Less opaque for inactive
      ],
      'fill-outline-color': ['get', 'color'],
    },
  };

  // Style for geo area outlines
  const geoAreaOutlineLayer: LineLayer = {
    id: 'geo-areas-outline',
    type: 'line',
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'case',
        ['get', 'isActive'],
        3, // Thicker for active
        2, // Normal for inactive
      ],
      'line-opacity': 0.8,
    },
  };

  // Use organization's coordinates when available
  useEffect(() => {
    if (!organization || geocoded) return;

    // Check if organization has address with coordinates
    if (organization.address && isJsonObject(organization.address as JsonValue)) {
      const latitude = safeGetNumber(organization.address as any, 'latitude');
      const longitude = safeGetNumber(organization.address as any, 'longitude');
      
      if (latitude && longitude) {
        const newCenter = {
          lng: longitude,
          lat: latitude,
        };

        setMapCenter(newCenter);
        setGeocoded(true);

        // If map is already loaded, pan to the new center
        if (mapRef.current) {
          mapRef.current.getMap().panTo(newCenter);
        }
      } else {
        // No coordinates available, keep default center
        console.log('No coordinates available in organization address, using default center');
        setGeocoded(true);
      }
    } else {
      // No address or address is not an object, keep default center
      console.log('No valid address in organization, using default center');
      setGeocoded(true);
    }
  }, [organization, geocoded, mapRef]);

  // Handle click on map features
  const onClick = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      if (!event.features || event.features.length === 0) return;

      // Get the clicked feature
      const feature = event.features[0];
      const zoneId = feature.properties?.zoneId;

      if (zoneId) {
        onZoneSelect(zoneId);
      }
    },
    [onZoneSelect],
  );

  // Function handlers for mousemove and mouseleave events
  const onMouseMove = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      const zoneId = e.features[0].properties?.zoneId;
      if (zoneId) {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = 'pointer';
        }
        setHighlightedZoneId(zoneId);
      }
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = '';
    }
    setHighlightedZoneId(null);
  }, []);

  // Add click handlers to map layers
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const layers = ['radius-zones', 'zip-code-zones', 'geo-areas'];

    // Add click handler for each layer
    layers.forEach((layer) => {
      map.on('click', layer, onClick);
    });

    // Add mouseover/mouseout handlers for hover effect
    layers.forEach((layer) => {
      map.on('mousemove', layer, onMouseMove);
      map.on('mouseleave', layer, onMouseLeave);
    });

    return () => {
      // Clean up event listeners
      layers.forEach((layer) => {
        map.off('click', layer, onClick);
        map.off('mousemove', layer, onMouseMove);
        map.off('mouseleave', layer, onMouseLeave);
      });
    };
  }, [onClick, onMouseMove, onMouseLeave, mapRef]);

  // Symbol layers for zip code zone labels
  const zipCodeLabelsLayer = {
    id: 'zip-code-labels',
    type: 'symbol',
    paint: {
      'text-color': [
        'case',
        ['get', 'isActive'],
        '#FFFFFF', // White for active
        '#1F2937', // Dark gray for inactive
      ],
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 0.5,
    },
    layout: {
      'text-field': ['get', 'zoneName'],
      'text-font': ['Open Sans Regular'],
      'text-size': 12,
      'text-anchor': 'center',
      'text-justify': 'center',
    },
  };

  if (!mapboxAccessToken) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-center p-6">
          <h3 className="text-lg font-medium mb-2">Map Error</h3>
          <p className="text-gray-500">
            Mapbox access token not found. Please check your environment
            variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {!isPublic && (
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium">Zone Map</h3>
        </div>
      )}

      <div
        ref={mapContainerRef}
        className="relative border border-gray-200 rounded-lg overflow-hidden"
        style={{ height: '600px', width: '100%' }}
      >
        <Map
          ref={mapRef}
          initialViewState={{
            longitude: mapCenter.lng,
            latitude: mapCenter.lat,
            zoom: mapZoom,
          }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={mapboxAccessToken}
          interactiveLayerIds={['radius-zones', 'zip-code-zones', 'geo-areas']}
          onLoad={() => {
            // Resize the map after loading to ensure proper rendering
            if (mapRef.current) {
              setTimeout(() => {
                mapRef.current?.resize();
                mapRef.current?.getMap().panTo(mapCenter);
              }, 100);
            }
          }}
        >
          <NavigationControl position="top-right" />

          {/* Radius-based zones */}
          <Source
            id="radius-zones-source"
            type="geojson"
            data={radiusBasedZonesGeoJSON}
          >
            <Layer {...radiusZoneLayer} />
            <Layer {...radiusZoneOutlineLayer} />
          </Source>

          {/* ZIP code zones */}
          <Source
            id="zip-code-zones-source"
            type="geojson"
            data={zipCodeZonesGeoJSON}
          >
            <Layer {...zipCodeZoneLayer} />
            <Layer {...(zipCodeLabelsLayer as any)} />
          </Source>

          {/* Geo areas */}
          <Source id="geo-areas-source" type="geojson" data={geoAreasGeoJSON}>
            <Layer {...geoAreaLayer} />
            <Layer {...geoAreaOutlineLayer} />
          </Source>
        </Map>
      </div>

      {/* Zone legend and information */}
      {!isPublic && (
        <div className="mt-2 flex flex-wrap gap-3">
          <Badge variant="outline" className="flex items-center">
            <CircleDot className="h-3 w-3 mr-1" />
            <span>{radiusBasedZones.length} Radius Zones</span>
          </Badge>
          <Badge variant="outline" className="flex items-center">
            <FileArchive className="h-3 w-3 mr-1" />
            <span>{zipCodeBasedZones.length} ZIP Code Zones</span>
          </Badge>
          <Badge variant="outline" className="flex items-center">
            <MapPin className="h-3 w-3 mr-1" />
            <span>{zonesWithGeoAreas.length} Geo Area Zones</span>
          </Badge>
          <Badge variant="outline" className="flex items-center ml-auto">
            <Info className="h-3 w-3 mr-1" />
            <span>Click on a zone to select it</span>
          </Badge>
        </div>
      )}
    </div>
  );
};

export default ZoneMapVisualization; 
